/**
 * FlowVariableMentionChip - React component for rendering @flowVariable mentions in tiptap editor
 *
 * Displays flow variable mentions as styled chips with the @ref format.
 * Green color scheme to distinguish from blue links and orange countdowns.
 * Follows CountdownMentionChip pattern exactly.
 */

import React, { memo } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { Box, Typography } from '@mui/material';
import DataObjectIcon from '@mui/icons-material/DataObject';

const chipSx = {
  backgroundColor: '#E8F5E9',
  color: '#1B5E20',
  padding: '2px 8px',
  borderRadius: '12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontWeight: 500,
  cursor: 'default',
  userSelect: 'none' as const,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  '&:hover': {
    backgroundColor: '#C8E6C9',
  },
};

const iconSx = {
  fontSize: '0.875rem',
  opacity: 0.7,
};

const tagMarkerSx = {
  color: '#666',
  fontSize: '0.75em',
  fontFamily: 'monospace',
  verticalAlign: 'baseline',
};

export const FlowVariableMentionChip = memo(function FlowVariableMentionChip({
  node,
}: NodeViewProps) {
  const { ref, labelTag } = node.attrs;

  return (
    <NodeViewWrapper
      as="span"
      className="flow-variable-mention-wrapper"
      contentEditable={false}
      draggable
      style={{
        display: 'inline-block',
        margin: '0 2px',
        verticalAlign: 'baseline',
      }}
    >
      {labelTag && (
        <Box component="span" sx={tagMarkerSx}>{`<${labelTag}>`}</Box>
      )}
      <Box
        component="span"
        className="flow-variable-mention-chip"
        data-flow-var-ref={ref}
        sx={chipSx}
      >
        <DataObjectIcon sx={iconSx} />
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 'inherit', fontSize: 'inherit' }}
        >
          @{ref}
        </Typography>
      </Box>
      {labelTag && (
        <Box component="span" sx={tagMarkerSx}>{`</${labelTag}>`}</Box>
      )}
    </NodeViewWrapper>
  );
});

export default FlowVariableMentionChip;
